// checkoutStore.js
// Drives the app-wide <CheckoutSheet />: the Stripe EMBEDDED checkout that opens
// as a bottom sheet ON the current page instead of navigating to
// checkout.stripe.com.
//
// WHY: the live sales call lives in a ref inside GlobalCallProvider, which is
// mounted ABOVE the router. Route changes don't touch it — but
// `window.location.replace(stripeUrl)` destroys the whole React tree and with it
// the WebRTC session. Every checkout used to end the call for exactly that
// reason. Opening the sheet from here keeps the page (and the call) alive.
//
// The sheet is mounted once at the App root, so ANY screen can start a checkout
// with `useCheckoutStore.getState().openCheckout({...})` — no prop drilling and
// no dependency on which route is active.
import { create } from "zustand";

export const useCheckoutStore = create((set) => ({
  open: false,
  // From the backend's `-embedded` endpoints.
  clientSecret: null,
  sessionId: null,
  // Hosted-checkout url for the SAME purchase. Used only when the embedded
  // iframe can't mount (old WebView without third-party cookies / DOM storage):
  // the sheet ends the call and redirects, i.e. the old behaviour. Having it
  // up-front means that bail-out costs no extra network round-trip.
  hostedUrl: null,
  // { agentId, userId, planName, value, currency, next, extraParams }
  //   next        → where to SPA-navigate after a successful payment
  //   extraParams → query params to carry onto `next` (fromCheckout, ispayg, …)
  meta: {},

  openCheckout: ({ clientSecret, sessionId, hostedUrl = null, meta = {} } = {}) =>
    set({ open: true, clientSecret, sessionId, hostedUrl, meta }),

  // Clears the secret too — a consumed/abandoned session must never be
  // re-mounted into a fresh iframe.
  closeCheckout: () =>
    set({ open: false, clientSecret: null, sessionId: null, hostedUrl: null, meta: {} }),
}));

export default useCheckoutStore;
